import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, CalendarClock } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { PLANS, SIZES, ADDONS, REGISTER_URL, fmtSize, fmtMoney } from "@/data/plans";

export default function ChallengeMatrix() {
  const [planId, setPlanId] = useState("two-step");
  const [size, setSize] = useState(50000);

  const plan = PLANS.find((p) => p.id === planId);
  const sizes = plan.sizes || SIZES;
  const activeSize = sizes.includes(size) ? size : sizes[sizes.length - 1];
  const total = plan.fees[activeSize];

  return (
    <section id="challenges" className="py-24 lg:py-32 relative" data-testid="challenge-matrix-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-[#FFD700]/[0.04] blur-[160px]" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal>
          <div className="mb-10 mx-auto max-w-3xl flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instant-light-banner"
              className="group w-full sm:w-auto flex items-center justify-center gap-3 rounded-full border border-[#FFD700]/35 bg-[#FFD700]/[0.07] px-6 py-3 transition-all duration-300 hover:border-[#FFD700]/60 hover:bg-[#FFD700]/[0.12] hover:shadow-[0_0_30px_rgba(255,215,0,0.12)]"
            >
              <Sparkles size={16} className="text-[#FFD700] shrink-0" />
              <span className="text-sm text-[#DBDFE8] text-center">
                <span className="font-bold gold-text">NEW:</span> Instant Funding Light — from{" "}
                <span className="text-white font-semibold">$85</span>. No evaluation, no profit target.
              </span>
            </a>
            <Link
              to="/swing-faq"
              data-testid="swing-banner"
              className="group w-full sm:w-auto flex items-center justify-center gap-3 rounded-full border border-[#2DACE2]/35 bg-[#2DACE2]/[0.07] px-6 py-3 transition-all duration-300 hover:border-[#2DACE2]/60 hover:bg-[#2DACE2]/[0.12] hover:shadow-[0_0_30px_rgba(45,172,226,0.12)]"
            >
              <CalendarClock size={16} className="text-[#2DACE2] shrink-0" />
              <span className="text-sm text-[#DBDFE8] text-center">
                <span className="font-bold text-[#2DACE2]">NEW:</span> Swing 2-Step — hold{" "}
                <span className="text-white font-semibold">overnight & weekends</span>. News trading allowed.
              </span>
            </Link>
          </div>
        </Reveal>
        <Reveal className="text-center flex flex-col items-center">
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">03</span>
            <span className="h-px w-10 bg-gradient-to-r from-[#FFD700] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#999999]">Challenge Matrix</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.04em] text-white">
            Pick your path to <span className="gold-text">funded.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#999999] max-w-xl">
            Five account types. Five sizes. One-time fee. Build it your way.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex justify-center" data-testid="challenge-account-type-tabs">
            <div className="inline-flex flex-wrap justify-center rounded-3xl sm:rounded-full bg-[#20222D]/70 border border-white/10 p-1.5 gap-1">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  data-testid={`plan-tab-${p.id}`}
                  onClick={() => setPlanId(p.id)}
                  className={`relative rounded-full px-4 sm:px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    planId === p.id ? "text-[#0D0E13]" : "text-[#DBDFE8] hover:text-white"
                  }`}
                >
                  {planId === p.id && (
                    <motion.span
                      layoutId="plan-pill"
                      className="absolute inset-0 rounded-full"
                      style={{ background: "linear-gradient(42deg, #FFD700 0%, #F1B434 100%)" }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{p.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex justify-center flex-wrap gap-3" data-testid="challenge-size-selector">
            {sizes.map((s) => (
              <button
                key={s}
                data-testid={`size-button-${s / 1000}k`}
                onClick={() => setSize(s)}
                className={`rounded-xl px-5 py-3 text-sm font-bold tracking-tight transition-all duration-300 border ${
                  activeSize === s
                    ? "border-[#FFD700]/60 bg-[#FFD700]/10 text-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.15)]"
                    : "border-white/10 bg-[#20222D]/50 text-[#999999] hover:border-white/25 hover:text-white"
                }`}
              >
                {fmtSize(s)}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-5 gap-6 items-start">
          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={planId}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-[-0.03em] text-white">
                      {plan.name} — {fmtSize(activeSize)}
                    </h3>
                    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#2DACE2] border border-[#2DACE2]/30 bg-[#2DACE2]/10 rounded-full px-3 py-1">
                      {plan.tag}
                    </span>
                  </div>
                  <dl className="divide-y divide-white/5" data-testid="challenge-specs-table">
                    {plan.specs.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between py-3.5 gap-4">
                        <dt className="text-sm text-[#999999]">{k}</dt>
                        <dd className="text-sm font-semibold text-white text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  {planId === "swing" && (
                    <Link
                      to="/swing-faq"
                      data-testid="swing-conditions-link"
                      className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2DACE2] hover:text-white transition-colors"
                    >
                      Read the full Swing 2-Step conditions
                      <ArrowRight size={15} />
                    </Link>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>

          <Reveal delay={0.25} className="lg:col-span-2">
            <div className="glass-card rounded-2xl p-6 sm:p-8 border-[#FFD700]/25 lg:sticky lg:top-28">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#999999] mb-5">
                Available Add-Ons at Checkout
              </div>
              <div className="space-y-3 mb-8">
                {ADDONS.map((a) => (
                  <div
                    key={a.id}
                    data-testid={a.id === "split" ? "addon-profit-split-info" : "addon-drawdown-limit-info"}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#030B16]/50 px-4 py-3.5"
                  >
                    <span className="text-sm font-medium text-[#DBDFE8]">{a.label}</span>
                    <span className="text-xs font-bold text-[#FFD700] whitespace-nowrap">+20% fee</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#999999] -mt-4 mb-8 leading-relaxed">
                Add-ons can be applied during registration on our checkout portal.
              </p>

              <div className="border-t border-white/10 pt-6">
                <div className="flex items-end justify-between mb-1">
                  <span className="text-sm text-[#999999]">One-time fee</span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={total}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                      data-testid="challenge-final-price-display"
                      className="text-4xl font-extrabold tracking-[-0.04em] gold-text"
                    >
                      {fmtMoney(total)}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className="text-xs text-[#999999] mb-6 text-right">
                  One-time fee · add-ons applied at checkout
                </div>
                <a
                  href={REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="challenge-buy-now-button"
                  className="btn-gold group w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-bold"
                >
                  Start {plan.name} — {fmtSize(activeSize)}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <p className="mt-4 text-center text-xs text-[#999999]">
                  Free $997/yr Discord mentorship included instantly
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
